/**
 * SPARC Organic pairing corpus from one shared Drive folder.
 * Keyword harvest per desk. Tagged source only. Does not write into tenant RAG.
 */

import { useSyncExternalStore } from 'react';
import {
  listGoogleDriveItems,
  requestGoogleDriveAccessToken,
  type GoogleDriveListItem,
} from '@tracekit/core/utils/googlePicker';

export const SPARC_ORGANIC_DRIVE_SOURCE = 'sparc-organic-drive' as const;
export const SPARC_ORGANIC_DRIVE_FOLDER_ID = '0AK-kiJZhDNYMUk9PVA';
export const SPARC_ORGANIC_DRIVE_PER_CLUSTER = 50;
export const SPARC_ORGANIC_DRIVE_MAX_FILES = 400;
export const SPARC_ORGANIC_DRIVE_KEYWORDS_PER_DESK = 12;

const FOLDER_MIME = 'application/vnd.google-apps.folder';

export type OrganicDriveFile = {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string;
  clusterId?: string;
};

export type DriveHarvestKeywords = Array<{ clusterId: string; keywords: string[] }>;

export const DEFAULT_HARVEST: DriveHarvestKeywords = [
  {
    clusterId: 'geoai',
    keywords: [
      'geoai',
      'satellite',
      'geospatial',
      'sar',
      'ndvi',
      'insar',
      'coherence',
      'mosaic',
      'tsunami',
      'landslide',
      'change detection',
      'remote sensing',
    ],
  },
  {
    clusterId: 'esg',
    keywords: [
      'esg',
      'climate',
      'emissions',
      'mrv',
      'compliance',
      'mrv protocol',
      'scope 1',
      'contaminated',
      'environmental assessment',
      'ghg',
      'carbon stock',
      'scope 3',
    ],
  },
  {
    clusterId: 'sovereign',
    keywords: [
      'infrastructure',
      'govearner',
      'public sector',
      'policy',
      'launch',
      'ports',
      'spaceport',
      'grid',
      'subsea',
      'ground station',
      'gnss',
      'data centre',
    ],
  },
  {
    clusterId: 'mine',
    keywords: [
      'mining',
      'mine',
      'tailings',
      'pit',
      'ore',
      'tailings dam',
      'slurry',
      'leach',
      'copper',
      'lithium',
      'rare earth',
      'rehabilitation',
    ],
  },
  {
    clusterId: 'agri',
    keywords: [
      'agriculture',
      'crop',
      'farmer',
      'irrigation',
      'yield',
      'flood',
      'drought',
      'hail',
      'livestock',
      'grain',
      'soil moisture',
      'harvest',
    ],
  },
  {
    clusterId: 'forest',
    keywords: [
      'forest',
      'timber',
      'canopy',
      'eudr',
      'wildfire',
      'fire',
      'deforestation',
      'peat',
      'salvage',
      'bark beetle',
      'fuel load',
      'concession',
    ],
  },
  {
    clusterId: 'defence',
    keywords: [
      'defence',
      'defense',
      'military',
      'nato',
      'osint',
      'maritime',
      'geoint',
      'isr',
      'border',
      'corridor',
      'domain awareness',
      'uav',
    ],
  },
  {
    clusterId: 'procurement',
    keywords: [
      'rfp',
      'solicitation',
      'jaggaer',
      'procurement',
      'govscope',
      'rfi',
      'rfq',
      'idiq',
      'gsa',
      'standing offer',
      'municipal gis',
      'evaluation criteria',
    ],
  },
];

let files: OrganicDriveFile[] = [];
let revision = 0;
let lastError: string | null = null;
const listeners = new Set<() => void>();

export const ORGANIC_DRIVE_TOKEN_KEY = 'sparc-organic-drive-token';
const DRIVE_TOKEN_TTL_MS = 55 * 60 * 1000;
const DRIVE_TOKEN_SKEW_MS = 15_000;

type CachedDriveToken = {
  accessToken: string;
  expiresAt: number;
};

let memoryToken: CachedDriveToken | null = null;

function emit(): void {
  revision += 1;
  listeners.forEach((listener) => listener());
}

export function listOrganicDriveFiles(): OrganicDriveFile[] {
  return files;
}

export function getOrganicDriveRevision(): number {
  return revision;
}

export function getOrganicDriveError(): string | null {
  return lastError;
}

export function subscribeOrganicDrive(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => listeners.delete(onStoreChange);
}

export function driveFileFingerprint(name: string): string {
  return name
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function dedupeOrganicDriveFiles(rows: OrganicDriveFile[]): OrganicDriveFile[] {
  const seenId = new Set<string>();
  const seenName = new Set<string>();
  const out: OrganicDriveFile[] = [];
  for (const file of rows) {
    if (!file.id || !file.name) continue;
    if (seenId.has(file.id)) continue;
    const fingerprint = driveFileFingerprint(file.name);
    if (fingerprint && seenName.has(fingerprint)) continue;
    seenId.add(file.id);
    if (fingerprint) seenName.add(fingerprint);
    out.push(file);
  }
  return out;
}

export function setOrganicDriveFiles(next: OrganicDriveFile[]): void {
  files = dedupeOrganicDriveFiles(next);
  lastError = null;
  emit();
}

export function clearOrganicDriveFiles(): void {
  files = [];
  lastError = null;
  emit();
}

export function readOrganicDriveToken(now = Date.now()): string | null {
  const valid = (row: CachedDriveToken | null): string | null => {
    if (!row?.accessToken || row.expiresAt <= now + DRIVE_TOKEN_SKEW_MS) return null;
    return row.accessToken;
  };
  const fromMemory = valid(memoryToken);
  if (fromMemory) return fromMemory;
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(ORGANIC_DRIVE_TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedDriveToken;
    const token = valid(parsed);
    if (token) {
      memoryToken = parsed;
      return token;
    }
  } catch {
    return null;
  }
  return null;
}

export function writeOrganicDriveToken(
  accessToken: string,
  expiresAt = Date.now() + DRIVE_TOKEN_TTL_MS,
): void {
  memoryToken = { accessToken, expiresAt };
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(ORGANIC_DRIVE_TOKEN_KEY, JSON.stringify(memoryToken));
  } catch {
    // Quota is not a reason to prompt Google again.
  }
}

export function clearOrganicDriveToken(): void {
  memoryToken = null;
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(ORGANIC_DRIVE_TOKEN_KEY);
  } catch {
    return;
  }
}

async function resolveDriveAccessToken(): Promise<string> {
  const cached = readOrganicDriveToken();
  if (cached) return cached;
  const accessToken = await driveApi.requestAccessToken('');
  writeOrganicDriveToken(accessToken);
  return accessToken;
}

export function useOrganicDriveRevision(): number {
  return useSyncExternalStore(subscribeOrganicDrive, getOrganicDriveRevision, () => 0);
}

export function desksToHarvestKeywords(
  desks: Record<string, { keywords: string[] }>,
): DriveHarvestKeywords {
  return Object.entries(desks).map(([clusterId, row]) => ({
    clusterId,
    keywords: row.keywords.slice(0, SPARC_ORGANIC_DRIVE_KEYWORDS_PER_DESK),
  }));
}

export function groupedOrganicDriveFiles(
  rows: OrganicDriveFile[] = files,
): Record<string, OrganicDriveFile[]> {
  const groups: Record<string, OrganicDriveFile[]> = {};
  for (const file of rows) {
    const key = file.clusterId?.trim() || 'unassigned';
    if (!groups[key]) groups[key] = [];
    groups[key].push(file);
  }
  return groups;
}

export function organicDriveCountsByCluster(
  rows: OrganicDriveFile[] = files,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const file of rows) {
    const key = file.clusterId?.trim() || 'unassigned';
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

type SearchDriveItems = (input: {
  accessToken: string;
  driveId: string;
  keyword: string;
  pageSize?: number;
}) => Promise<{ items: GoogleDriveListItem[] }>;

type OrganicDriveApi = {
  requestAccessToken: typeof requestGoogleDriveAccessToken;
  listItems: typeof listGoogleDriveItems;
  searchItems: SearchDriveItems;
};

async function defaultSearchItems(input: {
  accessToken: string;
  driveId: string;
  keyword: string;
  pageSize?: number;
}): Promise<{ items: GoogleDriveListItem[] }> {
  const keyword = input.keyword.trim();
  if (!keyword) return { items: [] };
  const escaped = keyword.replace(/'/g, "\\'");
  const q = `(fullText contains '${escaped}' or name contains '${escaped}') and trashed=false and mimeType != '${FOLDER_MIME}'`;
  const params = new URLSearchParams({
    q,
    fields: 'files(id,name,mimeType,modifiedTime)',
    pageSize: String(input.pageSize ?? SPARC_ORGANIC_DRIVE_PER_CLUSTER),
    corpora: 'drive',
    driveId: input.driveId,
    includeItemsFromAllDrives: 'true',
    supportsAllDrives: 'true',
  });
  const res = await fetch(`https://www.googleapis.com/drive/v3/files?${params}`, {
    headers: { Authorization: `Bearer ${input.accessToken}` },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Drive API search failed (${res.status}): ${body.slice(0, 200)}`);
  }
  const payload = (await res.json()) as {
    files?: Array<{ id: string; name: string; mimeType: string; modifiedTime?: string }>;
  };
  const items: GoogleDriveListItem[] = (payload.files ?? []).map((file) => ({
    id: file.id,
    name: file.name,
    mimeType: file.mimeType,
    modifiedTime: file.modifiedTime,
    isFolder: file.mimeType === FOLDER_MIME,
  }));
  return { items };
}

let driveApi: OrganicDriveApi = {
  requestAccessToken: requestGoogleDriveAccessToken,
  listItems: listGoogleDriveItems,
  searchItems: defaultSearchItems,
};

export function setOrganicDriveApi(next: Partial<OrganicDriveApi>): void {
  driveApi = { ...driveApi, ...next };
}

export function resetOrganicDriveApi(): void {
  driveApi = {
    requestAccessToken: requestGoogleDriveAccessToken,
    listItems: listGoogleDriveItems,
    searchItems: defaultSearchItems,
  };
}

function toDriveFile(item: GoogleDriveListItem, clusterId?: string): OrganicDriveFile {
  return {
    id: item.id,
    name: item.name,
    mimeType: item.mimeType,
    modifiedTime: item.modifiedTime,
    clusterId,
  };
}

async function listFolderFiles(
  accessToken: string,
  parentId: string,
  remaining: number,
  depth: number,
): Promise<OrganicDriveFile[]> {
  if (remaining <= 0) return [];
  const out: OrganicDriveFile[] = [];
  let pageToken: string | undefined;
  do {
    const result = await driveApi.listItems({
      accessToken,
      parentId,
      pageToken,
      pageSize: 100,
    });
    for (const item of result.items) {
      if (out.length >= remaining) break;
      if (item.isFolder) {
        if (depth >= 2) continue;
        const nested = await listFolderFiles(accessToken, item.id, remaining - out.length, depth + 1);
        out.push(...nested);
        continue;
      }
      out.push(toDriveFile(item));
    }
    pageToken = result.nextPageToken;
  } while (pageToken && out.length < remaining);
  return out.slice(0, remaining);
}

async function harvestByKeywords(
  accessToken: string,
  driveId: string,
  keywordSets: DriveHarvestKeywords,
): Promise<OrganicDriveFile[]> {
  const seen = new Set<string>();
  const out: OrganicDriveFile[] = [];
  for (const desk of keywordSets) {
    let taken = 0;
    for (const keyword of desk.keywords) {
      if (taken >= SPARC_ORGANIC_DRIVE_PER_CLUSTER) break;
      if (out.length >= SPARC_ORGANIC_DRIVE_MAX_FILES) break;
      const needle = keyword.trim();
      if (!needle) continue;
      const result = await driveApi.searchItems({
        accessToken,
        driveId,
        keyword: needle,
        pageSize: SPARC_ORGANIC_DRIVE_PER_CLUSTER,
      });
      for (const item of result.items) {
        if (item.isFolder || !item.id || seen.has(item.id)) continue;
        seen.add(item.id);
        out.push(toDriveFile(item, desk.clusterId));
        taken += 1;
        if (taken >= SPARC_ORGANIC_DRIVE_PER_CLUSTER) break;
        if (out.length >= SPARC_ORGANIC_DRIVE_MAX_FILES) break;
      }
    }
  }
  return out;
}

export function organicDriveNeedsConnect(): boolean {
  return !readOrganicDriveToken();
}

export async function loadOrganicDriveFolder(
  folderId = SPARC_ORGANIC_DRIVE_FOLDER_ID,
  keywordSets: DriveHarvestKeywords = DEFAULT_HARVEST,
): Promise<OrganicDriveFile[]> {
  const driveId = folderId.trim() || SPARC_ORGANIC_DRIVE_FOLDER_ID;
  try {
    const accessToken = await resolveDriveAccessToken();
    let listed: OrganicDriveFile[] = [];
    try {
      listed = await harvestByKeywords(accessToken, driveId, keywordSets);
    } catch {
      listed = [];
    }
    if (listed.length === 0) {
      listed = await listFolderFiles(accessToken, driveId, SPARC_ORGANIC_DRIVE_MAX_FILES, 0);
    }
    setOrganicDriveFiles(listed);
    return listed;
  } catch (error) {
    lastError = error instanceof Error ? error.message : 'Google Drive did not load.';
    emit();
    throw error;
  }
}

export async function tryLoadOrganicDriveFolder(
  folderId = SPARC_ORGANIC_DRIVE_FOLDER_ID,
  keywordSets?: DriveHarvestKeywords,
): Promise<OrganicDriveFile[]> {
  if (!readOrganicDriveToken()) return files;
  try {
    return await loadOrganicDriveFolder(folderId, keywordSets);
  } catch {
    return files;
  }
}
